import React, { type ReactNode, useState, useCallback } from 'react'
import classNames from 'classnames'
import { Placeholder, Stack } from 'react-bootstrap'
import { Button } from '../../../components/Button/Button'
import { useAppDispatch } from '../../../store/hooks'
import { addToast } from '../../../store/toast/toastSlice'
import styles from './PostConnectStepFrame.module.scss'

export interface PostConnectStepSkipAction {
  label: string
  onClick: () => void
  disabled?: boolean
}

export interface PostConnectStepFrameProps {
  onSave?: () => Promise<void> | void
  onAdvance?: () => void
  onBack?: () => void
  nextLabel?: string
  isNextDisabled?: boolean
  loading?: boolean
  skipAction?: PostConnectStepSkipAction
  children: ReactNode
}

export const PostConnectStepFrame: React.FC<PostConnectStepFrameProps> = ({
  onSave,
  onAdvance,
  onBack,
  nextLabel = 'Next',
  isNextDisabled = false,
  loading = false,
  skipAction,
  children,
}) => {
  const dispatch = useAppDispatch()
  const [isProcessing, setIsProcessing] = useState(false)

  const handleNext = useCallback(async () => {
    setIsProcessing(true)
    try {
      if (onSave) {
        await onSave()
      }
      onAdvance?.()
    } catch {
      dispatch(addToast({ variant: 'error', messages: ['Something went wrong. If the issue persists, please contact us at support@zocks.io'] }))
    } finally {
      setIsProcessing(false)
    }
  }, [onSave, onAdvance, dispatch])

  return (
    <div className={classNames(styles.content, 'col-12 col-sm-10 col-md-7 col-lg-5 position-relative')}>
      <Stack gap={4}>
        {loading ? (
          <>
            <Placeholder as='div' animation='glow' className='d-flex flex-column align-items-center gap-2 py-4'>
              <Placeholder xs={3} size='lg' />
              <Placeholder xs={7} size='lg' />
              <Placeholder xs={9} />
            </Placeholder>
            <Stack gap={2}>
              <Placeholder animation='glow' as='div' className='rounded border p-3'>
                <Placeholder xs={12} size='lg' />
              </Placeholder>
              <Placeholder animation='glow' as='div' className='rounded border p-3'>
                <Placeholder xs={12} size='lg' />
              </Placeholder>
            </Stack>
          </>
        ) : (
          children
        )}
        <div className='d-flex justify-content-center gap-3'>
          {onBack ? (
            <Button variant='grayGhost' onClick={onBack} disabled={isProcessing || loading}>
              Back
            </Button>
          ) : null}
          {skipAction ? (
            <Button variant='grayGhost' onClick={skipAction.onClick} disabled={skipAction.disabled || isProcessing || loading}>
              {skipAction.label}
            </Button>
          ) : null}
          <Button variant='primary' onClick={handleNext} loading={isProcessing} disabled={isProcessing || isNextDisabled || loading}>
            {nextLabel}
          </Button>
        </div>
      </Stack>
    </div>
  )
}
